import {parseISO} from "date-fns";
import i18n from "../i18n/i18n";
import plLocale from "date-fns/locale/pl";
import enLocale from "date-fns/locale/en-US";
import moment from "moment-timezone";

export class DateUtils {
    private static timeZone = "Europe/Warsaw";

    public static localeMap = {
        pl: plLocale,
        en: enLocale
    };

    public static dateMaskMap = {
        pl: "__.__.____",
        en: "__/__/____"
    };

    public static timeMaskMap = {
        pl: "__:__",
        en: "__:__ _m"
    };

    public static dateAndTimeMaskMap = {
        pl: "__.__.____ __:__",
        en: "__/__/____ __:__ _m"
    }

    public static getTimeFromString(time: string | null): string {
        if (!time) {
            return "*";
        }

        return time.substring(0, time.lastIndexOf(":"));
    }

    public static getDateWithDayName(date: string): string {
        const newDate = new Date(date);
        const weekDay = newDate.getDay();

        return `${this.getDayName(weekDay)} ${this.getFormattedDate(newDate)}`;
    }

    private static getDayName(day: number): string | undefined {
        switch (day) {
            case 0:
                return i18n.t("weekdays.sunday");
            case 1:
                return i18n.t("weekdays.monday");
            case 2:
                return i18n.t("weekdays.tuesday");
            case 3:
                return i18n.t("weekdays.wednesday");
            case 4:
                return i18n.t("weekdays.thursday");
            case 5:
                return i18n.t("weekdays.friday");
            case 6:
                return i18n.t("weekdays.saturday");
        }
    }

    public static getFormattedDate(date: any): string {
        if (!date) {
            return "";
        }

        return moment(date).tz(this.timeZone).format(i18n.t("dateFormat"));
    }

    public static getFormattedDateWithTime(date: any): string {
        if (!date) {
            return "";
        }

        return moment(date).tz(this.timeZone).format(i18n.t("dateFormatWithTime"));
    }

    public static getFormattedTimeWithDate(date: any): string {
        if (!date) {
            return "";
        }

        return moment(date).tz(this.timeZone).format(i18n.t("timeWithDateFormat"));
    }

    public static getFormattedDateWithTimeInUTC(date: any): string {
        if (!date) {
            return "";
        }

        const tmpDate = new Date(date.getTime());

        const year = moment(tmpDate).format("yyy");
        const month = moment(tmpDate).format("MM");
        const day = moment(tmpDate).format("DD");
        const hour = moment(tmpDate).format("HH");
        const minutes = moment(tmpDate).format("mm");


        const dateAndTimeWithPolishTimeZone = moment.tz(`${year}-${month}-${day} ${hour}:${minutes}:00`, this.timeZone);

        return dateAndTimeWithPolishTimeZone.utc().toDate().toISOString();
    }

    public static joinDateAndTimeToUtc(date: Date | null, time: Date | null): Date | null {
        if (!date || !time || !date.getDate || !time.getHours) {
            return null;
        }

        const year = moment(date).format("yyy");
        const month = moment(date).format("MM");
        const day = moment(date).format("DD");
        const hour = moment(time).format("HH");
        const minutes = moment(time).format("mm");

        const dateAndTimeWithPolishTimeZone = moment.tz(`${year}-${month}-${day} ${hour}:${minutes}`, this.timeZone);

        return dateAndTimeWithPolishTimeZone.utc().toDate();
    }

    public static parseLocalDate(date: string): Date {
        return parseISO(date);
    }

    public static getDateAndTimeInPolishTimeZone(date: Date | null | string): Date | null {
        if (!date) {
            return null;
        }

        const newDate = new Date(date);

        const dateInPolishTimeZone = moment.tz(newDate, this.timeZone);
        const localOffset = newDate.getTimezoneOffset();
        const localOffsetInMillisecond = this.changeMinutesToMilliseconds(localOffset);
        const offsetInPoland = dateInPolishTimeZone.utcOffset();
        const offsetInPolandInMillisecond = this.changeMinutesToMilliseconds(offsetInPoland);

        return new Date(newDate.getTime() + localOffsetInMillisecond + offsetInPolandInMillisecond);
    }

    private static changeMinutesToMilliseconds(minutes: number): number {
        return minutes * 60 * 1000;
    }

    public static getTimeIntervalFromDate(date: Date): { startDate: string, endDate: string } {
        if (!date) {
            return {startDate: "", endDate: ""};
        }

        const startDate = new Date(date.getTime());
        const year = moment(startDate).format("yyy");
        const month = moment(startDate).format("MM");
        const day = moment(startDate).format("DD");

        const nextDay = new Date(date.getTime());
        nextDay!.setDate(nextDay!.getDate() + 1);
        const yearOfNextDay = moment(nextDay).format("yyy");
        const monthOfNextDay = moment(nextDay).format("MM");
        const dayOfNextDay = moment(nextDay).format("DD");

        const startDateAndTimeWithPolishTimeZone = moment.tz(`${year}-${month}-${day} 00:00:00`, this.timeZone);
        const endDateAndTimeWithPolishTimeZone = moment.tz(`${yearOfNextDay}-${monthOfNextDay}-${dayOfNextDay} 00:00:00`, this.timeZone);

        return {
            startDate: startDateAndTimeWithPolishTimeZone.utc().toDate().toISOString(),
            endDate: endDateAndTimeWithPolishTimeZone.utc().toDate().toISOString()
        };
    }

    public static getMonthName(month: number): string | undefined {
        switch (month) {
            case 1:
                return "january";
            case 2:
                return "february";
            case 3:
                return "march";
            case 4:
                return "april";
            case 5:
                return "may";
            case 6:
                return "june";
            case 7:
                return "july";
            case 8:
                return "august";
            case 9:
                return "september";
            case 10:
                return "october";
            case 11:
                return "november";
            case 12:
                return "december";
        }
    }

    public static isDateGreaterThanToday(date: Date | string): boolean {
        const today = new Date(Date.now()).getTime();
        const dateTime = new Date(date).getTime();

        return dateTime > today;
    }

    public static getDateWithoutTimeToUtc(date: any): string {
        if (!date || !date.getTime) {
            return "";
        }

        const dateTmp = new Date(date.getTime());
        const year = moment(dateTmp).format("yyy");
        const month = moment(dateTmp).format("MM");
        const day = moment(dateTmp).format("DD");

        const dateAndTimeWithPolishTimeZone = moment.tz(`${year}-${month}-${day} 00:00:00`, this.timeZone);

        return dateAndTimeWithPolishTimeZone.utc().toDate().toISOString();
    }
}
